/**
 * Turns an app's subscriptions into answers (docs/vendor-apps-contract.md §3).
 *
 * Runs only on clinician action — Comprehend (note generate) or a refresh —
 * never on a timer. One subscription = one Flex++ call to the existing
 * /v1/conversation/question endpoint via apiClient.getConversationQuestion.
 * Cache key is hash(structure) + contextVersion + patientId, so a Refresh with
 * nothing new is free.
 *
 * `mode: 'mock'` (Dev Console) answers from the structure's types and a
 * scenario without touching the backend — same code path otherwise.
 */
import { coerceAnswers, unwrapSummary } from './flexCompile';

const VENDOR_QUESTION_PREFIX = 'vendor_app';

export function createEvaluator({ store, apiClient, mode = 'live', mockScenario = () => 'none' }) {
  const inflight = new Map(); // `${appId}:${subId}` → Promise

  async function answerOne(appId, subId, sub) {
    if (mode === 'mock') return sub.summary ? { answers: { summary: mockSummary(mockScenario()) }, errors: [] } : mockAnswer(sub.compiled, sub.types, mockScenario());
    const rsp = await apiClient.getConversationQuestion(
      store.conversationId,
      `${VENDOR_QUESTION_PREFIX}:${appId}:${subId}`,
      `Answer the following structured questions about this visit for the ${store.getApp(appId)?.name || 'vendor'} app.`,
      'Flex',
      [],
      null,
      false,
      sub.compiled,
    );
    if (!rsp || rsp.error) {
      const err = new Error(rsp?.error?.message || 'evaluation failed');
      err.code = /rate limit/i.test(err.message) ? 'RATE_LIMITED' : 'EVALUATION_FAILED';
      throw err;
    }
    let answer = rsp.answer;
    if (typeof answer === 'string') {
      try {
        answer = JSON.parse(answer);
      } catch (e) {
        /* leave as string; coerce will stringify */
      }
    }
    return { answers: answer, errors: Array.isArray(rsp.flexErrors) ? rsp.flexErrors.map((e) => ({ path: e.Field || e.path, code: 'CHUNK_FAILED' })) : [] };
  }

  /**
   * Evaluate every subscription for `appId` (or just `onlySubId`) against the
   * open chart. `deliver(subId, payload)` is the bridge's sendAnswers;
   * `fail(subId, code, message)` its sendError.
   */
  async function evaluate(appId, { deliver, fail, onlySubId = null, reason = 'refresh' } = {}) {
    const patient = store.patient;
    const subs = store.subsFor(appId);
    if (!subs.size) return { evaluated: 0, cached: 0 };
    if (!patient || (mode !== 'mock' && !store.conversationId)) {
      for (const [subId] of subs) if (!onlySubId || subId === onlySubId) fail?.(subId, 'NO_ENCOUNTER', 'no visit is open');
      return { evaluated: 0, cached: 0 };
    }
    const contextVersion = store.contextVersion;
    const conversationId = store.conversationId;
    const counts = { evaluated: 0, cached: 0 };
    const jobs = [];
    for (const [subId, sub] of subs) {
      if (onlySubId && subId !== onlySubId) continue;
      const key = `${sub.hash}|${contextVersion}|${patient.id}|${conversationId}`;
      if (sub.lastKey === key && sub.lastResult) {
        counts.cached += 1;
        deliver?.(subId, sub.lastResult);
        continue;
      }
      const inflightKey = `${appId}:${subId}`;
      if (inflight.has(inflightKey)) continue;
      const job = runOne({ appId, subId, sub, key, patient, conversationId, contextVersion, deliver, fail, counts }).finally(() => inflight.delete(inflightKey));
      inflight.set(inflightKey, job);
      jobs.push(job);
    }
    await Promise.all(jobs);
    store._setStatus(appId, { lastEvaluatedAt: new Date().toISOString(), lastEvaluateReason: reason });
    return { ...counts };
  }

  async function runOne({ appId, subId, sub, key, patient, conversationId, contextVersion, deliver, fail, counts }) {
    try {
      const { answers, errors } = await answerOne(appId, subId, sub);
      const payload = {
        patientId: patient.id,
        answers: sub.summary ? unwrapSummary(answers) : coerceAnswers(answers, sub.types),
        contextVersion,
        answeredAt: new Date().toISOString(),
        partial: errors.length > 0,
        errors,
      };
      // Only cache/deliver if the chart didn't change under us.
      if (store.patient?.id === patient.id && store.conversationId === conversationId && store.subsFor(appId).get(subId) === sub) {
        sub.lastKey = key;
        sub.lastResult = payload;
        deliver?.(subId, payload);
      }
      counts.evaluated += 1;
    } catch (e) {
      fail?.(subId, e.code || 'EVALUATION_FAILED', e.message);
    }
  }

  return { evaluate };
}

// ---- mock mode -------------------------------------------------------------
// Answers are derived from leaf names + declared types so any vendor structure
// gets a plausible, shape-correct response. Scenario nudges the RTM/HEP/pain
// leaves so the three Dev Console transcripts produce visibly different pushes.
export const MOCK_SCENARIOS = ['rtm', 'hep', 'none'];

export const mockSummary = (scenario) =>
  scenario === 'none'
    ? ''
    : scenario === 'rtm'
      ? 'Reviewed remote monitoring data with the patient; adherence discussed and program continued unchanged.'
      : 'Patient reported doing the program most days but skipped hip hinge because of knee pain; PT regressed it and progressed sit-to-stand to 3x12.';

export function mockAnswer(compiled, types, scenario = 'none') {
  const leafValue = (path, type) => {
    const key = path.toLowerCase();
    const mentions = (w) => key.includes(w);
    switch (type) {
      case 'boolean': {
        if (mentions('rtm') || mentions('remote')) return scenario === 'rtm';
        if (mentions('hep') || mentions('exercise') || mentions('test')) return scenario !== 'none';
        if (scenario === 'none') return false;
        // Per-patient structures ("performed.sit-to-stand": boolean) — vary by key so the demo shows a mix.
        let h = 0;
        for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
        return (h & 1) === 0;
      }
      case 'number':
        if (mentions('pain')) return scenario === 'none' ? null : 4;
        return scenario === 'none' ? null : 3;
      case 'string[]':
        if (mentions('hep') || mentions('exercise')) return scenario === 'hep' ? ['sit-to-stand 3x12', 'bridge 2x10 (added)'] : [];
        return [];
      case 'number[]':
        return scenario === 'none' ? [] : [3, 4];
      default:
        if (mentions('site') || mentions('region')) return scenario === 'none' ? null : 'left knee';
        if (mentions('barrier') || mentions('reason')) return scenario === 'none' ? 'none' : 'pain';
        if (mentions('action')) return 'modified';
        if (mentions('exercise') || mentions('name')) return 'sit-to-stand';
        if (mentions('dosage') || mentions('reps') || mentions('sets')) return '3x12';
        return scenario === 'none' ? null : 'discussed briefly';
    }
  };

  function walk(node, path) {
    // Typed leaves first: `string[]` / `number[]` leaves compile to a list on
    // the wire but are answered as a plain array.
    if (Object.prototype.hasOwnProperty.call(types, path)) return leafValue(path, types[path]);
    if (Array.isArray(node)) {
      if (scenario === 'none') return [];
      const rows = scenario === 'hep' ? 2 : 1;
      return Array.from({ length: rows }, () => walk(node[0], `${path}[]`));
    }
    if (node && typeof node === 'object') {
      const out = {};
      for (const key of Object.keys(node)) {
        if (key === 'flex++' || key === 'prune_early' || key === '_existingValue') continue;
        out[key] = walk(node[key], path ? `${path}.${key}` : key);
      }
      return out;
    }
    return null;
  }

  return { answers: walk(compiled, ''), errors: [] };
}
